import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../environments/environment';
import { RolesApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';
import { LoginModalComponent } from '../login-modal/login-modal.component';

export interface FooterLink {
  label: string;
  url: string;
  external?: boolean;
  requiresAuth?: boolean;
}

export interface SocialLink {
  icon: string;
  url: string;
  label: string;
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterModule, LoginModalComponent],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
})
export class FooterComponent {
  private rolesService = inject(RolesApiService);
  private toasterService = inject(ToasterService);
  private router = inject(Router);
  private keycloak = inject(KeycloakService);

  currentYear = new Date().getFullYear();
  catalogueUiUrl = environment.catalogueUiUrl;
  email = environment.email;

  importantLinks: FooterLink[] = [
    { label: 'Dashboard', url: '/consumer', external: false, requiresAuth: true },
    { label: 'User Manual', url: '/user-manual', external: false },
    { label: 'Help and support', url: environment.helpDeskUrl, external: true },
    { label: 'Contact us', url: '/contact-us', external: false, requiresAuth: true },
  ];

  developerLinks: FooterLink[] = [
    { label: 'Control Plane API docs', url: environment.controlPlaneApiDocsUrl, external: true },
    { label: 'File Server API docs', url: environment.fileServerApiDocsUrl, external: true },
    { label: 'Resource Server API docs', url: environment.resourceServerApiDocsUrl, external: true },
    // { label: 'OGC Server API docs', url: environment.ogcServerApiDocsUrl, external: true },
    // { label: 'Sandbox Server API docs', url: environment.sandboxServerApiDocsUrl, external: true }
  ];

  // Icon paths from public folder
  globalIcon = '/global.svg';
  emailIcon = '/sms.svg';

  footerFrameIcon = '/footer-frame.svg';
  showLoginModal = false;
  loginRedirectUri = '';

  /** Hide partner logo image on load error (e.g. missing file). */
  onLogoError(event: Event): void {
    const el = event.target as HTMLImageElement | null;
    if (el) el.style.display = 'none';
  }

  /** Initiative partners shown in the footer bottom bar. */
  initiativeLogos: { name: string; src: string; url: string; useFilter?: boolean }[] = [
    { name: 'AIAIC', src: '/partner-logo/AIAIC Logo 2 1.svg', url: 'https://aiaic.maharashtra.gov.in/' },
    { name: 'PoCRA', src: '/new_pocra.png', url: 'https://mahapocra.gov.in/en', useFilter: false },
    {
      name: 'Department of Agriculture',
      src: '/partner-logo/WhatsApp Image 2025-12-01 at 14.34.45 2.svg',
      url: 'https://krishi.maharashtra.gov.in/',
      useFilter: false,
    },
  ];

  /** Fourth column: World Bank, IISc, CDPG logos. */
  partnerLogos: { name: string; src: string; url: string }[] = [
    { name: 'World Bank', src: '/partner-logo/WorldBank-logo.png', url: 'https://www.worldbank.org/ext/en/home' },
    { name: 'IISc', src: '/partner-logo/IISC-Logo-White-e1666353947123 2.svg', url: 'https://iisc.ac.in/' },
    { name: 'CDPG', src: '/cdpg.svg', url: 'https://dataforpublicgood.org.in/' },
  ];

  socialLinks: SocialLink[] = [
    {
      icon: '/linkedin_symbol.svg.svg',
      url: 'https://www.linkedin.com/company/mahaagx',
      label: 'LinkedIn'
    },
    {
      icon: '/github_symbol.svg.svg',
      url: 'https://github.com/mahaagx',
      label: 'GitHub'
    },
    {
      icon: 'twitter',
      url: 'https://twitter.com/mahaagx',
      label: 'X (Twitter)'
    },
    {
      icon: '/facebook_symbol.svg.svg',
      url: 'https://facebook.com/mahaagx',
      label: 'Facebook'
    },
    {
      icon: '/youtube_symbol.svg.svg',
      url: 'https://youtube.com/@mahaagx',
      label: 'YouTube'
    }
  ];

  private getRedirectUri(path: string): string {
    return typeof window !== 'undefined' && window.location?.origin
      ? `${window.location.origin}${path}`
      : path;
  }

  private openLoginModal(redirectPath: string): void {
    this.loginRedirectUri = this.getRedirectUri(redirectPath);
    this.showLoginModal = true;
  }

  /**
   * Check if user is logged in
   */
  private async checkLoginStatus(): Promise<boolean> {
    try {
      return await this.keycloak.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      return false;
    }
  }

  /**
   * Handle click on auth-gated important links (Dashboard, Contact us).
   * Shows login modal when unauthenticated; navigates when logged in.
   */
  async onAuthGatedLinkClick(event: Event, link: FooterLink): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    try {
      const isLoggedIn = await this.checkLoginStatus();
      if (!isLoggedIn) {
        const redirectPath = link.label === 'Dashboard' ? '/consumer' : (link.url || '/');
        this.openLoginModal(redirectPath);
        return;
      }

      if (link.label === 'Dashboard') {
        await this.rolesService.getRoles();
        const userRoles = this.rolesService.getUserRoles();
        const route = this.rolesService.getHighestRolePanelRoute(userRoles || []);
        this.router.navigate([route]);
        return;
      }

      if (link.url) {
        this.router.navigate([link.url]);
      }
    } catch (error) {
      console.error(`Error navigating from footer link "${link.label}":`, error);
      if (link.label === 'Dashboard') {
        this.toasterService.warning('Unable to open dashboard. Please try again.');
      }
    }
  }
}


